import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";

function Counter({ to, prefix = "", suffix = "", duration = 2, decimals = 0 }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.4 });

  useEffect(() => {
    if (!inView) return;
    let raf;
    const start = performance.now();
    const step = (now) => {
      const t = Math.min(1, (now - start) / (duration * 1000));
      const eased = 1 - Math.pow(1 - t, 3);
      setVal(eased * to);
      if (t < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [inView, to, duration]);

  return (
    <span ref={ref} className="ticker-digit">
      {prefix}
      {val.toFixed(decimals)}
      {suffix}
    </span>
  );
}

const stats = [
  {
    label: "Target MOIC",
    value: 3.2,
    suffix: "x",
    decimals: 1,
    caption: "Blended across venture + growth mandates.",
  },
  {
    label: "Target IRR",
    value: 22,
    suffix: "%",
    caption: "Net of fees, reported quarterly.",
  },
  {
    label: "Impact Framework",
    value: 5,
    caption: "SDGs anchored per active mandate.",
  },
  {
    label: "Cost Advantage",
    value: 55,
    suffix: "%",
    caption: "Lower than in-house build over 3 years.",
  },
];

export default function Metrics() {
  return (
    <section
      className="relative py-24 md:py-32 border-t border-[var(--line)]"
      data-testid="metrics-section"
    >
      <div className="grain absolute inset-0" />
      <div className="relative mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="text-[10px] tracking-eyebrow uppercase text-gold mb-4">
          VII. Outcomes
        </div>
        <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-4xl">
          Numbers we underwrite. Numbers we report.
        </h2>

        <div className="mt-12 md:mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          {stats.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.8, delay: i * 0.08 }}
              className="relative border border-[var(--line)] p-8 md:p-10 min-h-[240px] flex flex-col justify-between hover:border-emerald/40 transition-colors"
              data-testid={`metric-${i}`}
            >
              <div className="text-[10px] tracking-eyebrow uppercase text-ivory-dim">
                {s.label}
              </div>
              <div className="font-display text-6xl md:text-7xl text-gold leading-none tracking-tight">
                <Counter to={s.value} decimals={s.decimals || 0} suffix={s.suffix || ""} />
              </div>
              <div className="text-sm text-ivory/60 leading-relaxed">
                {s.caption}
              </div>
              <div className="absolute top-4 right-4 text-[10px] tracking-eyebrow uppercase text-ivory-dim">
                0{i + 1}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
